import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  ICellRendererParams,
  RowNode,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  EditableCheckboxRenderer,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { RootState } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { ISODateFormat } from '@northfork/basics'
import { InlineEditDisplayOptions } from '@northfork/ng-basics'
import { isEqual, omit } from 'lodash-es'
import {
  Basis,
  MvtBasis,
  commonBasisTypes,
} from '../../../../model/mvt-basis.model'
import { Security } from '../../../../model/security.model'
import {
  AmortOverrideType,
  amortOverrideTypesVal,
} from '../../../../model/single-transaction/amort-override-types'
import { ExternalUnmatchedTransaction } from '../../../../model/single-transaction/external-unmatched-transaction'
import {
  ExternalMvt,
  MvtData,
} from '../../../../model/single-transaction/market-value-transfer.model'
import { TransactionModalInput } from '../../../../model/single-transaction/popup-input.model'
import { MarketValueTransferRecordType } from '../../../../model/single-transaction/security-transfer/market-value-transfer-record-type.model'
import { initialLoadField } from '../../../../model/single-transaction/security-transfer/security-transfer-grid.models'
import {
  BasisGridEdit,
  BasisGridExternal,
  SecurityGrid,
} from '../../../../model/single-transaction/security-transfer/security-transfer.model'
import { TransferOptions } from '../../../../model/single-transaction/security-transfer/transfer-options.model'
import { TransferPurchaseInfo } from '../../../../model/single-transaction/security-transfer/transfer-purchase-info.model'
import { ValidationBasisData } from '../../../../model/single-transaction/validation-view.model'
import { TranCategory } from '../../../../model/transaction-category.model'
import {
  ENTRIES,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../../model/transaction-type.model'
import { CopyService } from '../../../../services/copy.service'
import { CreateEditTransactionService } from '../../../../services/create-edit-transaction.service'
import { PasteService } from '../../../../services/single-transaction/paste.service'
import { Calculators } from '../../../../util/calculators.util'
import { LOAN_FIELD_COLUMNS } from '../../../../util/loan-field.util'
import {
  ACCOUNT_REQUIRED_FIELD_WARNING,
  addWarningDetails,
  calculateBasisPrice,
  clearAllInvalidations,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  formatNumberWithThousandSeparator,
  getStringISOForDate,
  handleValidation,
} from '../../../../util/common-utils'
import { TransactionConstants } from '../../../../util/constants'
import { getInitialLoadData } from '../../../../util/security-transfer.initial-data'
@Component({
  selector: 'hs-security-basis-transfer',
  templateUrl: './security-transfer-basis-grid.component.html',
  styleUrls: ['./security-transfer-basis-grid.component.scss'],
  providers: [PasteService, CopyService],
})
export class SecurityTransferBasisGridComponent implements OnDestroy {
  private _aggregateAccountUnresolved = false
  constructor(
    readonly createEditTransactionService: CreateEditTransactionService,
    readonly store: Store<RootState>,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
  ) {}

  private columns: ColDef[] = this.getDefaultDef()
  @Input() transInput?: TransactionModalInput
  @Input() hplMode?: boolean
  @Input() set basisData(
    basisData: BasisGridEdit | BasisGridExternal | SecurityGrid | undefined,
  ) {
    if (!basisData) return
    this.rowData
      .filter(row => row.field.includes('Date') && row.default != '')
      .forEach(rowFiltered => {
        rowFiltered.default = basisData.reconDate.toString()
      })
    this.columns = this.getDefaultDef()
    if ('tranType' in basisData) {
      if (basisData.tpiMap) this.tpiMap = basisData.tpiMap
      this.updateBasisGridEdit(basisData)
    } else if ('transaction' in basisData) {
      this.updateBasisGridExt(basisData)
    } else {
      const mvtData = {
        category: TranCategory.TRANSFER,
        accountId: basisData.accountDetails?.accountId,
        tranType: 'TRANSFER_IN' as TransactionTypeKey,
      }
      this.createEditTransactionService.loadMvtValues(mvtData).subscribe(
        mvtBasis => {
          this.setBasis(mvtBasis)
          this.defaultBasisId = basisData.accountDetails?.defaultBasis ?? 1
        },
        err => {
          this.errorText = TransactionConstants.ERROR_LOAD_BASIS
          this.errorDetails = err.error
        },
      )
    }
  }
  @Output()
  basisFieldChanged = new EventEmitter<void>()
  @Output()
  costChanged = new EventEmitter<CellValueChangedEvent>()
  @Output()
  allLoadedBasis = new EventEmitter<void>()
  @Output()
  editingStarted = new EventEmitter<void>()
  @Output()
  editingStopped = new EventEmitter<void>()

  @Output() gridFocused = new EventEmitter<string>()

  gridApi?: AgGridApi
  tpiMap?: Map<string, TransferPurchaseInfo>
  mvtDataMap?: Map<string, MvtData>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowData: any[] = getInitialLoadData(getStringISOForDate(new Date()))
  amortOverrideType: AmortOverrideType[] = amortOverrideTypesVal
  dateFormat = ISODateFormat
  formatNumbers = '1.2-16'
  selectedTypeofTransfer = TransactionConstants.TRANSFER_OPTIONS[0]

  rowNodeMap = new Map<string, RowNode>()
  basisTypes?: Basis[]
  security?: Security
  defaultBasisId = 1
  loadingText = TransactionConstants.LOAD_BASIS
  errorText = ''
  errorDetails: unknown

  loadTable = false
  gridOptions: GridOptions = {
    ...heliosSizingAdjustments,
    onGridReady: event => {
      this.gridApi = event
      this.copyService.registerGrid(event.api)
      this.pasteService.registerGrid(event.api, event.columnApi)
    },
    defaultColDef: {
      menuTabs: [],
      sortable: false,
    },
    enableBrowserTooltips: true,
    enableCellChangeFlash: true,
    enableRangeSelection: true,
    suppressMultiRangeSelection: false,
    enableFillHandle: true,
    fillHandleDirection: 'y',
    onCellEditingStarted: () => {
      this.editingStarted.emit()
    },
    onCellEditingStopped: () => {
      this.editingStopped.emit()
    },
    onCellKeyDown: (event: CellKeyDownEvent) => {
      if (event.api.getEditingCells().length == 0) {
        emptySelectedCellsOnValidKeyPress(
          (event.event as KeyboardEvent).key,
          event.api,
        )
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getRowNodeId: ({ id }: any) => id,
    sideBar: false,
    suppressLoadingOverlay: false,
    columnDefs: this.columns,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFirstDataRendered: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onGridSizeChanged: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    onCellValueChanged: (event: CellValueChangedEvent) => {
      this.onColumnValueChanged(event)
    },
    onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
      const element = event.event?.target as HTMLElement
      if (element.className == 'ag-fill-handle') {
        fillHandleDoubleClickHandlerSingleDialog(event.api)
      } else {
        event.api.startEditingCell({
          rowIndex: event.rowIndex ?? 0,
          colKey: event.column.getColId(),
        })
      }
    },
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
  }

  @HostListener('click') basisGridClicked() {
    this.gridFocused.emit('basis')
  }

  private getDefaultDef(): ColDef[] {
    return [
      {
        field: initialLoadField('isEditable'),
        hide: true,
      },
      {
        field: initialLoadField('errorMessage'),
        hide: true,
      },
      {
        field: initialLoadField('validityStatus'),
        headerName: '',
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
      },
      {
        field: initialLoadField('field'),
        headerName: 'Field',
        maxWidth: 155,
        minWidth: 155,
      },
      {
        field: initialLoadField('default'),
        headerName: 'Default',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRenderer: undefined,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellRendererSelector: params => {
          const isCheckboxRow = this.checkBoxCell(params)
          const component = isCheckboxRow
            ? EditableCheckboxRenderer
            : NgGridComponents.inlineEditDisplay
          return {
            component,
            params,
          }
        },
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  setBasis(mvtBasis: MvtBasis) {
    this.basisTypes = mvtBasis.basis.sort((a: Basis, b: Basis) =>
      a.prefix.localeCompare(b.prefix),
    )
    this.basisTypes.forEach(basis => {
      this.rowData.forEach(row => {
        row[`isEditable${basis.prefix}`] = row.id == 'overrideForBasis'
        row[basis.prefix.toLowerCase()] =
          typeof row.default == 'boolean' ? false : ''
      })
      this.addDynamicColumn(basis)
      this.gridApi?.api.setColumnDefs(this.columns)
    })
    this.loadTable = true
    this.gridApi?.api.sizeColumnsToFit()
    this.allLoadedBasis.emit()
  }

  updateBasisGridExt(basisData: BasisGridExternal) {
    this.defaultBasisId = basisData.accountDetails?.defaultBasis ?? 1
    const mvtData = {
      category: TranCategory.TRANSFER,
      accountId: basisData.accountDetails?.accountId,
      tranType:
        basisData.transaction.tranType ?? ('TRANSFER_IN' as TransactionTypeKey),
    }

    this.createEditTransactionService.loadMvtValues(mvtData).subscribe(
      mvtBasis => {
        this.basisTypes = mvtBasis.basis
        this.basisTypes.sort((a: Basis, b: Basis) =>
          a.prefix.localeCompare(b.prefix),
        )
        if (
          this.basisTypes?.length > 0 &&
          basisData.transaction.accountingBasesToTpi
        ) {
          this.populateDefaultColumn(
            basisData.transaction.accountingBasesToTpi[this.defaultBasisId],
          )
          if (
            basisData.transaction.accountingBasesToTpi[this.defaultBasisId]
              .origPrice == null
          ) {
            this.setBasisCostAndPrice(
              basisData.transaction,
              this.defaultBasisId,
              '',
            )
          }
          this.basisTypes.forEach((basis: Basis) => {
            if (basisData.transaction.accountingBasesToTpi) {
              const purchaseInfoData =
                basisData.transaction.accountingBasesToTpi[basis.basisId] ??
                basisData.transaction.accountingBasesToTpi[1]

              if (purchaseInfoData) {
                this.createRowsLegacyTransaction(basis.prefix, purchaseInfoData)
                this.populateMvtRows(basis.prefix)
                if (
                  purchaseInfoData.origPrice ||
                  this.defaultBasisId == basis.basisId
                ) {
                  this.setBasisCostAndPrice(
                    basisData.transaction,
                    basis.basisId,
                    basis.prefix,
                  )
                }
                this.adjustEqualDefaultBasis(
                  basis,
                  purchaseInfoData,
                  new Map(
                    Object.entries<TransferPurchaseInfo>(
                      basisData.transaction.accountingBasesToTpi,
                    ),
                  ),
                )
              } else {
                this.resetDataforNonPurchaseInfoData(basis.prefix)
              }
            }
            this.addDynamicColumn(basis)
            this.gridApi?.api.setColumnDefs(this.columns)
          })
        } else {
          this.createRowsNonTpiTransaction(
            basisData.transaction,
            basisData.security,
          )
        }
        this.loadTable = true
        this.gridApi?.api.sizeColumnsToFit()
        this.allLoadedBasis.emit()
      },
      err => {
        this.errorText = TransactionConstants.ERROR_LOAD_BASIS
        this.errorDetails = err.error
      },
    )
  }

  setBasisCostAndPrice(
    transaction: ExternalUnmatchedTransaction,
    basisId: number,
    basisPrefix: string,
  ) {
    if (transaction.basisCostAndPrice?.[basisId]) {
      const originalPurchasePrice = this.rowData.find(
        element => element.id == 'origPrice',
      )
      const originalCost = this.rowData.find(
        element => element.id == 'origCost',
      )
      const derivedPrice = transaction.basisCostAndPrice[basisId].derivedPrice
      const derivedCost = transaction.basisCostAndPrice[basisId].derivedCost
      if (derivedPrice && derivedPrice != null) {
        originalPurchasePrice[this.getBasisProperty(basisPrefix)] = derivedPrice
      }
      if (derivedCost && derivedCost != null) {
        originalCost[this.getBasisProperty(basisPrefix)] = derivedCost
      }
    }
  }

  createRowsNonTpiTransaction(
    transaction: ExternalUnmatchedTransaction,
    security: Security,
  ) {
    const origTradeDate = this.rowData.find(
      element => element.id == 'origTradeDate',
    )
    const origSettleDate = this.rowData.find(
      element => element.id == 'origSettleDate',
    )
    const originalCommission = this.rowData.find(
      element => element.id == 'origCommission',
    )
    const originalPurchasePrice = this.rowData.find(
      element => element.id == 'origPrice',
    )
    const accruedInterest = this.rowData.find(
      element => element.id == 'accruedInterest',
    )
    const factor = this.rowData.find(element => element.id == 'factor')

    origTradeDate[`isEditable${this.defaultBasisId}`] = true
    origTradeDate[this.getBasisProperty('')] = transaction.entryDate ?? ''

    origSettleDate[`isEditable${this.defaultBasisId}`] = true
    origSettleDate[this.getBasisProperty('')] = transaction.settleDate ?? ''

    originalCommission[`isEditable${this.defaultBasisId}`] = true
    originalCommission[this.getBasisProperty('')] =
      transaction.commission ?? 0.0

    originalPurchasePrice[`isEditable${this.defaultBasisId}`] = true
    originalPurchasePrice[this.getBasisProperty('')] = transaction.price ?? ''

    if (security) {
      accruedInterest[`isEditable${this.defaultBasisId}`] = true
      accruedInterest[this.getBasisProperty('')] =
        security?.decimalAccrued ?? ''

      factor[`isEditable${this.defaultBasisId}`] = true
      factor[this.getBasisProperty('')] =
        Calculators.calculateCombinedFactor(security)
    }
  }

  updateBasisGridEdit(basisData: BasisGridEdit) {
    this.defaultBasisId = basisData.accountDetails?.defaultBasis ?? 1
    const mvtData = {
      category: TranCategory.TRANSFER,
      accountId: basisData.accountDetails?.accountId,
      tranType: basisData.tranType,
    }
    this.createEditTransactionService.loadMvtValues(mvtData).subscribe(
      mvtBasis => {
        this.basisTypes = mvtBasis.basis.sort((a: Basis, b: Basis) =>
          a.prefix.localeCompare(b.prefix),
        )

        if (ENTRIES.includes(TransactionTypeEnum[basisData.tranType])) {
          this.populateColumns(basisData.mvtMap, basisData.tpiMap)
        } else {
          this.populateColumns(basisData.mvtMap)
        }
        this.setupInitialData(this.rowData)
        this.loadTable = true
        this.gridApi?.api.sizeColumnsToFit()
        this.allLoadedBasis.emit()
      },
      err => {
        this.errorText = TransactionConstants.ERROR_LOAD_BASIS
        this.errorDetails = err.error
      },
    )
  }

  populateColumns(
    mvtMap?: Map<string, MvtData>,
    tpiMap?: Map<string, TransferPurchaseInfo>,
  ) {
    if (this.basisTypes?.length && this.basisTypes.length >= 1) {
      this.basisTypes.forEach((basis: Basis) => {
        this.rowData.forEach(row => {
          row[`isEditable${basis.prefix}`] = row.id == 'overrideForBasis'
          row[basis.prefix.toLowerCase()] =
            typeof row.default == 'boolean' ? false : ''
        })
        const purchaseInfoData =
          tpiMap?.get(basis.basisId.toString()) ?? tpiMap?.get('1') //GAAP type

        if (purchaseInfoData) {
          this.createRowsLegacyTransaction(basis.prefix, purchaseInfoData)
        } else {
          this.resetDataforNonPurchaseInfoData(basis.prefix)
        }

        const mvtDataBasis = mvtMap?.get(basis.basisId.toString())
        this.populateMvtRows(basis.prefix, mvtDataBasis)
        this.adjustEqualDefaultBasis(
          basis,
          purchaseInfoData,
          tpiMap,
          mvtDataBasis,
          mvtMap,
        )
        this.addDynamicColumn(basis)
        this.gridApi?.api.setColumnDefs(this.columns)
      })
    }

    this.populateDefaultColumn(
      tpiMap?.get(this.defaultBasisId.toString()) ?? tpiMap?.get('1'),
      mvtMap?.get(this.defaultBasisId.toString()),
    )
  }

  resetDataforNonPurchaseInfoData(basisPrefix: string) {
    this.rowData
      .filter(
        element =>
          element.id != 'overrideForBasis' &&
          element.id != 'marketValueTransfer' &&
          !element.field.includes('MVT'),
      )
      .forEach(element => {
        element[`isEditable${basisPrefix}`] = false
        element[this.getBasisProperty(basisPrefix)] = ''
      })
  }

  populateDefaultColumn(
    purchaseInfoData?: TransferPurchaseInfo,
    mvtDataDefault?: MvtData,
  ) {
    if (purchaseInfoData) {
      this.createRowsLegacyTransaction('', purchaseInfoData)
    } else {
      this.resetDataforNonPurchaseInfoData('')
    }

    this.populateMvtRows('', mvtDataDefault)
    const overrideBasis = this.rowData.find(
      element => element.id == 'overrideForBasis',
    )
    overrideBasis.isEditable = false
    overrideBasis.default = false
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      this.columnIsNotOneOfBases(event.column.getColId())
    ) {
      return
    }

    if (event.data.id == 'marketValueTransfer') {
      this.onChangeMarketValueTransfer(event)
    } else if (event.data.id == 'externalMvt') {
      this.onChangeExternalMvt(event)
    } else if (event.data.id == 'overrideForBasis') {
      this.onChangeOverrideForBasis(event)
    } else if (
      event.data.id == 'amortStartCost' ||
      event.data.id == 'origCost'
    ) {
      this.onChangeCost(event)
    }
    if (this.aggregateAccountUnresolved) {
      // No account yet: flag the edited field instead of triggering validation.
      addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
      event.api.refreshCells({ force: true, rowNodes: [event.node] })
    } else {
      this.basisFieldChanged.emit()
    }
  }
  columnIsNotOneOfBases(colId: string): boolean {
    if (colId == 'default') {
      return false
    }
    const hasMatchedBases = this.basisTypes?.find(
      basisEle => colId == this.getBasisProperty(basisEle.prefix),
    )

    return !hasMatchedBases
  }

  setTransferTypeSelected(transferType: TransferOptions) {
    this.selectedTypeofTransfer = transferType
    const overrideForBasisNode = this.getRowNode('overrideForBasis')
    if (
      this.selectedTypeofTransfer.name == TransactionTypeEnum.TRANSFER_OUT ||
      this.selectedTypeofTransfer.name == TransactionTypeEnum.EXCHANGE_OUT
    ) {
      this.setEditableStateForTpiFields('', false)
      this.basisTypes?.forEach(basisType => {
        this.setEditableStateForTpiFields(basisType.prefix, false)
      })
    } else {
      this.setEditableStateForTpiFields('', true)
      this.basisTypes?.forEach(basisType => {
        if (overrideForBasisNode?.data[this.getBasisProperty(basisType.prefix)])
          this.setEditableStateForTpiFields(basisType.prefix, true)
      })
    }
  }

  updateProceeds(units: number) {
    const transactionType = this.selectedTypeofTransfer.name

    const colVisible: ColDef[] = []

    //taking out those columns which are visible on the grid and for which we need to calculate proceeds
    this.columns.forEach(col => {
      if (col.field != 'field' && col.hide != true) {
        colVisible.push(col)
      }
    })

    colVisible.forEach(colName => {
      //picking one by one a column and storing all the data of cell in columnData
      if (colName.field) {
        const price =
          this.getRowNode('origPrice')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('origPrice')?.data[colName.field]
        const accruedInterest =
          this.getRowNode('accruedInterest')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('accruedInterest')?.data[colName.field]
        const factor =
          this.getRowNode('factor')?.data[colName.field] == ''
            ? 1.0
            : this.getRowNode('factor')?.data[colName.field]
        const commission =
          this.getRowNode('origCommission')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('origCommission')?.data[colName.field]
        const exitFee =
          this.getRowNode('exitFee')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('exitFee')?.data[colName.field]
        const exitFeeReceivable =
          this.getRowNode('exitFeeReceivable')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('exitFeeReceivable')?.data[colName.field]
        const prepaidInterest =
          this.getRowNode('prepaidInterest')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('prepaidInterest')?.data[colName.field]
        const interestDue =
          this.getRowNode('interestDue')?.data[colName.field] == ''
            ? 0.0
            : this.getRowNode('interestDue')?.data[colName.field]
        const reserveTotal = this.getReserveTotal(colName.field)

        let proceedValue = 0.0
        if (this.security) {
          proceedValue = Calculators.calculateProceeds(
            Number(price),
            Number(accruedInterest),
            units,
            Number(commission),
            0.0, //originationFees are not a part of security transfers
            0.0, //drawFee are not a part of security transfers
            exitFee,
            exitFeeReceivable,
            0.0, //prepaymentPenaltyFee are not a part of security transfers
            prepaidInterest,
            interestDue,
            reserveTotal,
            this.security,
            transactionType,
            Number(factor),
            Boolean(this.hplMode),
            // InventoryType[inventoryTypeKey as keyof typeof InventoryType],
          )
        }
        this.rowNodeMap
          .get('proceeds')
          ?.setDataValue(colName.field, proceedValue)
      }
    })
  }

  private getReserveTotal(basisField: string): number {
    const reserveFields = [
      'interestBearingInterestReserve',
      'interestBearingPrincipalReserve',
      'nonInterestBearingInterestReserve',
      'nonInterestBearingPrincipalReserve',
    ]
    return reserveFields.reduce((total, field) => {
      const value = this.getRowNode(field)?.data[basisField]
      return total + (value === '' || value == null ? 0.0 : Number(value))
    }, 0.0)
  }

  onChangeOverrideForBasis(event: CellValueChangedEvent) {
    const basisField = event.colDef.field
    if (basisField && event.colDef.field != 'default')
      this.populateRowDataForBasisColumns(basisField.toUpperCase(), event.value)
    this.setEditableStateForMVTFlag(
      !basisField || event.colDef.field == 'default'
        ? ''
        : basisField.toUpperCase(),
      event.value,
    )

    if (
      this.selectedTypeofTransfer.name != TransactionTypeEnum.TRANSFER_OUT &&
      this.selectedTypeofTransfer.name != TransactionTypeEnum.EXCHANGE_OUT
    ) {
      this.setEditableStateForTpiFields(
        !basisField || basisField == 'default' ? '' : basisField?.toUpperCase(),
        event.value,
      )
    }
  }

  onChangeCost(event: CellValueChangedEvent) {
    this.costChanged.emit(event)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupInitialData(rowData: any[]) {
    rowData.forEach(element => {
      const tableNode = this.getRowNode(element.id)
      if (tableNode) {
        tableNode.data = element
        this.gridApi?.api.redrawRows({ rowNodes: [tableNode] })
      }
    })
  }

  onChangeMarketValueTransfer(event: CellValueChangedEvent) {
    this.setEditableStateForExternalMVTFlag(
      !event.colDef.field || event.colDef.field == 'default'
        ? ''
        : event.colDef.field?.toUpperCase(),
      event.value,
    )
  }

  onChangeExternalMvt(event: CellValueChangedEvent) {
    this.setEditableStateForExternalMVTFields(
      !event.colDef.field || event.colDef.field == 'default'
        ? ''
        : event.colDef.field?.toUpperCase(),
      event.value,
    )
  }

  checkBoxCell(params: ICellRendererParams): boolean {
    if (
      params.data.id == 'overrideForBasis' ||
      params.data.id == 'marketValueTransfer' ||
      params.data.id == 'externalMvt'
    ) {
      return true
    } else return false
  }

  editInlineColumns(params: ICellEditorParams) {
    switch (params.data.id) {
      case 'externalMvtDate':
      case 'origTradeDate':
      case 'origSettleDate':
      case 'amortStartDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'externalMvtPrice':
      case 'origPrice':
      case 'origCost':
      case 'origCommission':
      case 'accumulatedOtti':
      case 'proceeds':
      case 'yield':
      case 'accruedInterest':
      case 'factor':
      case 'daysImpaired':
      case 'accruedAtLoad':
      case 'amortStartCost':
      case 'amortStartPrice':
      case 'taxDaysWashSale':
      case 'washSaleDeferredLoss':
      case 'accruedMarketDiscount':
      case 'fxOriginalPurchaseRate':
      case 'fxOriginalCost':
      case 'fxAccumulatedOtti':
      case 'fxBookValueAtLoad':
      case 'fxAccruedAtLoad':
      case 'fxAccruedMarketDiscount':
      case 'fxWashSaleDeferredLoss':
      case 'originalOriginationFee':
      case 'exitFee':
      case 'exitFeeReceivable':
      case 'drawFee':
      case 'prepaymentPenaltyFee':
      case 'prepaidInterest':
      case 'interestDue':
      case 'interestBearingInterestReserve':
      case 'interestBearingPrincipalReserve':
      case 'nonInterestBearingInterestReserve':
      case 'nonInterestBearingPrincipalReserve':
      case 'straddleDeferredLoss':
      case 'fxStraddleDeferredLoss':
      case 'section1256DeferredLoss':
      case 'fxSection1256DeferredLoss':
      case 'lpCumulativeEarnings':
      case 'lpCumulativeDistributions':
      case 'returnOfInvestment':
      case 'returnOnInvestment':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'amortOverride':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.amortOverrideType?.map(a => a.name),
          },
        }
      default:
        return {
          dataType: InlineEditDataType.String,
        }
    }
  }

  // While the parent's aggregate account is unresolved, basis fields stay editable but
  // validation is deferred and edited fields are flagged (the account picker lives in the
  // sibling generic grid). Clear the flags once a concrete sub-account is resolved.
  @Input() set aggregateAccountUnresolved(value: boolean) {
    this._aggregateAccountUnresolved = value
    if (!value) {
      clearAllInvalidations(this.gridApi?.api)
    }
    this.gridApi?.api.refreshCells({ force: true })
  }
  get aggregateAccountUnresolved(): boolean {
    return this._aggregateAccountUnresolved
  }

  editable(params: EditableCallbackParams): boolean {
    const getRowNodeById = params.api?.getRowNode(params.data['id'])
    if (getRowNodeById) {
      this.rowNodeMap.set(params.data['id'], getRowNodeById)
    }
    if (params.colDef.field == 'default') {
      return params.data.isEditable
    } else {
      return (
        params?.data[`isEditable${params.colDef.field?.toUpperCase()}`] ?? false
      )
    }
  }

  populateMvtRows(basisPrefix: string, mvtData?: MvtData) {
    const marketValueTransfer = this.rowData.find(
      element => element.id == 'marketValueTransfer',
    )
    const externalMvt = this.rowData.find(
      element => element.id == 'externalMvt',
    )
    const externalMvtDate = this.rowData.find(
      element => element.id == 'externalMvtDate',
    )
    const externalMvtPrice = this.rowData.find(
      element => element.id == 'externalMvtPrice',
    )
    const overrideBasis = this.rowData.find(
      element => element.id == 'overrideForBasis',
    )
    if (mvtData) {
      const mvtType = mvtData.recordType
      overrideBasis[`isEditable${basisPrefix}`] = true
      overrideBasis[this.getBasisProperty(basisPrefix)] = true
      marketValueTransfer[`isEditable${basisPrefix}`] = true
      if (mvtType != MarketValueTransferRecordType.StandardCopy) {
        externalMvt[`isEditable${basisPrefix}`] = true
        marketValueTransfer[this.getBasisProperty(basisPrefix)] = true

        if (mvtType == MarketValueTransferRecordType.StandardExternal) {
          externalMvt[this.getBasisProperty(basisPrefix)] = true

          externalMvtDate[`isEditable${basisPrefix}`] = true
          externalMvtDate[this.getBasisProperty(basisPrefix)] =
            mvtData.amortStartDate

          externalMvtPrice[this.getBasisProperty(basisPrefix)] = mvtData.price
          externalMvtPrice[`isEditable${basisPrefix}`] = true
        } else {
          externalMvtDate[this.getBasisProperty(basisPrefix)] = ''
          externalMvtPrice[this.getBasisProperty(basisPrefix)] = ''
        }
      }
    } else {
      marketValueTransfer[this.getBasisProperty(basisPrefix)] = false
      externalMvt[this.getBasisProperty(basisPrefix)] = false
      externalMvtDate[this.getBasisProperty(basisPrefix)] = ''
      externalMvtPrice[this.getBasisProperty(basisPrefix)] = ''
    }
  }

  createRowsLegacyTransaction(
    basisPrefix: string,
    legacyData: TransferPurchaseInfo,
  ) {
    this.rowData.forEach(element => {
      switch (element.id) {
        case 'overrideForBasis':
          element[`isEditable${basisPrefix}`] = true
          element[this.getBasisProperty(basisPrefix)] = true
          break
        case 'marketValueTransfer':
          element[`isEditable${basisPrefix}`] = true
          break
        case 'origTradeDate':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origTradeDate ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'origSettleDate':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origSettleDate ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'origPrice':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origPrice ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'origCost':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origCost ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'origCommission':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.commission ?? ''
          element[`isEditable${basisPrefix}`] = true

          break
        case 'accumulatedOtti':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.accumulatedOtti ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'proceeds':
          element[this.getBasisProperty(basisPrefix)] = ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'yield':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origYield ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'accruedInterest':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.origAccruedInt ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'factor':
          element[this.getBasisProperty(basisPrefix)] = legacyData.factor ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'daysImpaired':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.daysImpaired ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'accruedAtLoad':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.localAccruedAtLoad ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'amortStartCost':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.amortStartCost ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'amortStartDate':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.amortStartDate ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'amortStartPrice':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.amortStartPrice ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'amortOverride':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.amortOverride ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'taxDaysWashSale':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.taxDaysHeld ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'washSaleDeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.washSaleDeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'accruedMarketDiscount':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.accruedMarketDiscount ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxOriginalPurchaseRate':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxRateAtOrigEntry ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxOriginalCost':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxOrigCost ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxAccumulatedOtti':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxAccumulatedOtti ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxBookValueAtLoad':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxBookValueAtLoad ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxAccruedAtLoad':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxAccruedAtLoad ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxAccruedMarketDiscount':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxAccruedMarketDiscount ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxWashSaleDeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxWashSaleDeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'originalOriginationFee':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.originalOriginationFee ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'exitFee':
          element[this.getBasisProperty(basisPrefix)] = legacyData.exitFee ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'exitFeeReceivable':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.exitFeeReceivable ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'drawFee':
          element[this.getBasisProperty(basisPrefix)] = legacyData.drawFee ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'prepaymentPenaltyFee':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.prepaymentPenaltyFee ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'prepaidInterest':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.prepaidInterest ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'interestDue':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.interestDue ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'interestBearingInterestReserve':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.interestBearingInterestReserve ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'interestBearingPrincipalReserve':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.interestBearingPrincipalReserve ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'nonInterestBearingInterestReserve':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.nonInterestBearingInterestReserve ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'nonInterestBearingPrincipalReserve':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.nonInterestBearingPrincipalReserve ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'straddleDeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.straddleDeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxStraddleDeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxStraddleDeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'section1256DeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.section1256DeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'fxSection1256DeferredLoss':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.fxSection1256DeferredLoss ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'lpCumulativeEarnings':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.lpCumulativeEarnings ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'lpCumulativeDistributions':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.lpCumulativeDistributions ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'returnOfInvestment':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.returnOfInvestment ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        case 'returnOnInvestment':
          element[this.getBasisProperty(basisPrefix)] =
            legacyData.returnOnInvestment ?? ''
          element[`isEditable${basisPrefix}`] = true
          break
        default:
          break
      }
    })
  }

  getBasisProperty(basisPrefix: string): string {
    return basisPrefix == '' ? 'default' : basisPrefix.toLowerCase()
  }

  transferDateSync(date: string) {
    this.rowData.forEach(element => {
      switch (element.id) {
        case 'origTradeDate':
          element.default = date
          break
        case 'origSettleDate':
          element.default = date
          break
        case 'amortStartDate':
          element.default = ''
          break
      }
    })
    this.gridApi?.api.refreshCells()
  }

  transferPriceSync(price: string) {
    this.rowData.forEach(element => {
      switch (element.id) {
        case 'origPrice':
          element.default = price
          break
      }
    })
    this.gridApi?.api.refreshCells()
  }

  getRowNode(key: string): RowNode | undefined {
    return this.rowNodeMap.get(key)
  }

  getActiveTpis(units: number): { [key: number]: TransferPurchaseInfo } {
    const basisTypes = this.basisTypes ?? commonBasisTypes
    const tpiMap = {} as { [key: number]: TransferPurchaseInfo }
    const overrideForBasis = this.rowData.find(
      element => element.id == 'overrideForBasis',
    )
    basisTypes.forEach(basis => {
      if (
        this.defaultBasisId == basis.basisId &&
        !overrideForBasis[this.getBasisProperty(basis.prefix)]
      ) {
        tpiMap[basis.basisId] = this.saveRowsLegacyTransaction(
          'default',
          basis.basisId,
        )
        this.populateEssentialTpiFields(tpiMap[basis.basisId], units)
      } else if (overrideForBasis[this.getBasisProperty(basis.prefix)]) {
        tpiMap[basis.basisId] = this.saveRowsLegacyTransaction(
          basis.prefix,
          basis.basisId,
        )
        this.populateEssentialTpiFields(tpiMap[basis.basisId], units)
      }
    })
    return tpiMap
  }

  getActiveMvts(): MvtData[] {
    const mvtMap = {} as { [key: number]: MvtData }
    const mvtDataMap = this.mvtDataMap
    const basisTypes = this.basisTypes ?? commonBasisTypes
    if (!mvtDataMap) {
      return Object.values(mvtMap)
    }
    const marketValueTransfer = this.rowData.find(
      element => element.id == 'marketValueTransfer',
    )
    const externalMvt = this.rowData.find(
      element => element.id == 'externalMvt',
    )
    const externalMvtDate = this.rowData.find(
      element => element.id == 'externalMvtDate',
    )
    const externalMvtPrice = this.rowData.find(
      element => element.id == 'externalMvtPrice',
    )
    basisTypes.forEach(basisFiltered => {
      if (
        marketValueTransfer[this.getBasisProperty(basisFiltered.prefix)] ||
        marketValueTransfer['default']
      ) {
        const mvtDataBasis =
          mvtDataMap.get(basisFiltered.basisId.toString()) ??
          mvtDataMap.get(this.defaultBasisId.toString())
        if (mvtDataBasis) mvtMap[basisFiltered.basisId] = mvtDataBasis
        if (
          externalMvt[this.getBasisProperty(basisFiltered.prefix)] ||
          externalMvt['default']
        ) {
          const price =
            externalMvtPrice[this.getBasisProperty(basisFiltered.prefix)] ??
            externalMvtPrice['default']
          mvtMap[basisFiltered.basisId].price = price !== '' ? Number(price) : 0
          mvtMap[basisFiltered.basisId].amortStartDate =
            externalMvtDate[this.getBasisProperty(basisFiltered.prefix)] ??
            externalMvtDate['default']
        }
      }
    })

    return Object.values(mvtMap)
  }

  saveRowsLegacyTransaction(
    basisPrefix: string,
    basisId: number,
  ): TransferPurchaseInfo {
    const transData = {} as TransferPurchaseInfo
    transData.basisId = basisId
    const tpiId = this.tpiMap?.get(basisId.toString())
    transData.id = tpiId ? tpiId.id : 0
    this.rowData
      .filter(ele => ele[this.getBasisProperty(basisPrefix)] !== '')
      .forEach(element => {
        switch (element.id) {
          case 'origTradeDate':
            transData.origTradeDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'origSettleDate':
            transData.origSettleDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'origPrice':
            transData.origPrice = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'origCost':
            transData.origCost = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'origCommission':
            transData.commission = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'accumulatedOtti':
            transData.accumulatedOtti = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'yield':
            transData.origYield = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            transData.amortStartYield = transData.origYield
            break

          case 'accruedInterest':
            transData.origAccruedInt = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'factor':
            transData.factor = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'daysImpaired':
            transData.daysImpaired = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'accruedAtLoad':
            transData.fxAccruedAtLoad = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            transData.localAccruedAtLoad = transData.fxAccruedAtLoad
            break
          case 'amortStartCost':
            transData.amortStartCost = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'amortStartDate':
            transData.amortStartDate =
              element[this.getBasisProperty(basisPrefix)]
            transData.valuationDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'amortStartPrice':
            transData.amortStartPrice = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'amortOverride':
            transData.amortOverride =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'taxDaysWashSale':
            transData.taxDaysHeld = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'washSaleDeferredLoss':
            transData.washSaleDeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'accruedMarketDiscount':
            transData.accruedMarketDiscount = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxOriginalPurchaseRate':
            transData.fxRateAtOrigEntry = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxOriginalCost':
            transData.fxOrigCost = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxAccumulatedOtti':
            transData.fxAccumulatedOtti = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxBookValueAtLoad':
            transData.fxBookValueAtLoad = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxAccruedAtLoad':
            transData.fxAccruedAtLoad = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxAccruedMarketDiscount':
            transData.fxAccruedMarketDiscount = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxWashSaleDeferredLoss':
            transData.fxWashSaleDeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'originalOriginationFee':
            transData.originalOriginationFee = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'exitFee':
            transData.exitFee = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'exitFeeReceivable':
            transData.exitFeeReceivable = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'drawFee':
            transData.drawFee = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'prepaymentPenaltyFee':
            transData.prepaymentPenaltyFee = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'prepaidInterest':
            transData.prepaidInterest = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'interestDue':
            transData.interestDue = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'interestBearingInterestReserve':
            transData.interestBearingInterestReserve = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'interestBearingPrincipalReserve':
            transData.interestBearingPrincipalReserve = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'nonInterestBearingInterestReserve':
            transData.nonInterestBearingInterestReserve = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'nonInterestBearingPrincipalReserve':
            transData.nonInterestBearingPrincipalReserve = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'straddleDeferredLoss':
            transData.straddleDeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxStraddleDeferredLoss':
            transData.fxStraddleDeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'section1256DeferredLoss':
            transData.section1256DeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'fxSection1256DeferredLoss':
            transData.fxSection1256DeferredLoss = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'lpCumulativeEarnings':
            transData.lpCumulativeEarnings = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'lpCumulativeDistributions':
            transData.lpCumulativeDistributions = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'returnOfInvestment':
            transData.returnOfInvestment = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          case 'returnOnInvestment':
            transData.returnOnInvestment = Number(
              element[this.getBasisProperty(basisPrefix)],
            )
            break
          default:
            break
        }
      })
    return transData
  }

  populateEssentialTpiFields(tpi: TransferPurchaseInfo, units: number) {
    if (!tpi.factor && this.security)
      tpi.factor = Calculators.calculateCombinedFactor(this.security)
    if (!tpi.origAccruedInt && this.security)
      tpi.origAccruedInt = this.security.decimalAccrued ?? 0
    if (!tpi.origYield) {
      tpi.origYield = 0.0
      tpi.amortStartYield = 0.0
    }
    if (!tpi.amortStartDate) {
      tpi.amortStartDate = tpi.origSettleDate
    }
    if (!tpi.amortStartPrice) {
      if (tpi.amortStartCost && this.security && units) {
        tpi.amortStartPrice =
          calculateBasisPrice(
            tpi.amortStartCost,
            tpi.factor * units,
            this.security.securityMasterData.priceScalar ?? 0.01,
            this.security.securityMasterData.contractSize ?? 1.0,
          ) ?? 0
      } else {
        tpi.amortStartPrice = tpi.origPrice ?? 0
      }
    }
    if (!tpi.origCost) {
      tpi.origCost = this.calculateFxOrigCost(tpi, units)
    }
    if (!tpi.amortOverride) {
      tpi.amortOverride = 'OBEY_ACCOUNT_SECURITY'
    }
  }

  calculateFxOrigCost(
    tpi: TransferPurchaseInfo,
    units: number,
  ): number | undefined {
    const origRate = tpi.fxRateAtOrigEntry
    const origPrice = tpi.origPrice
    const factor = tpi.factor
    if (origRate && origPrice && factor) {
      return origRate * origPrice * units * factor
    }

    return undefined
  }

  getSelectedMvtBases(): Map<number, Basis> {
    const mvtBasesMap = new Map<number, Basis>()
    const marketValueTransfer = this.rowData.find(
      element => element.id == 'marketValueTransfer',
    )
    const overrideForBasis = this.rowData.find(
      element => element.id == 'overrideForBasis',
    )
    this.basisTypes?.forEach(basis => {
      const basisPrefix = basis.prefix
      if (
        (marketValueTransfer['default'] &&
          overrideForBasis[this.getBasisProperty(basisPrefix)] == false) ||
        marketValueTransfer[this.getBasisProperty(basisPrefix)]
      ) {
        mvtBasesMap.set(basis.basisId, basis)
      }
    })
    return mvtBasesMap
  }

  getSelectedExternalMvtBases(): Map<number, ExternalMvt> {
    const externalMvtBasesMap = new Map<number, ExternalMvt>()
    const externalMvt = this.rowData.find(
      element => element.id == 'externalMvt',
    )
    const externalMvtPrice = this.rowData.find(
      element => element.id == 'externalMvtPrice',
    )
    const externalMvtDate = this.rowData.find(
      element => element.id == 'externalMvtDate',
    )
    this.basisTypes?.forEach(basis => {
      const basisPrefix =
        this.defaultBasisId == basis.basisId ? 'default' : basis.prefix
      if (externalMvt[this.getBasisProperty(basisPrefix)]) {
        const externalMvtData: ExternalMvt = {
          price:
            externalMvtPrice[this.getBasisProperty(basisPrefix)] != ''
              ? Number(externalMvtPrice[this.getBasisProperty(basisPrefix)])
              : 0.0,
          date:
            externalMvtDate[this.getBasisProperty(basisPrefix)] != ''
              ? externalMvtDate[this.getBasisProperty(basisPrefix)]
              : getStringISOForDate(new Date()),
        }
        externalMvtBasesMap.set(basis.basisId, externalMvtData)
      } else if (externalMvt['default']) {
        const externalMvtData: ExternalMvt = {
          price:
            externalMvtPrice['default'] != ''
              ? Number(externalMvtPrice['default'])
              : 0.0,
          date:
            externalMvtDate['default'] != ''
              ? externalMvtDate['default']
              : getStringISOForDate(new Date()),
        }
        externalMvtBasesMap.set(basis.basisId, externalMvtData)
      }
    })
    return externalMvtBasesMap
  }

  markAllNodeValid() {
    this.rowNodeMap.forEach((value: RowNode) => {
      if (this.gridApi) this.handleValidation(value, 'VALID', '')
    })
  }

  handleValidation(
    rowNode: RowNode,
    validationState: string,
    validationMessage: string,
  ) {
    if (this.gridApi)
      handleValidation(rowNode, validationState, validationMessage)
  }

  createBasisTransactionData(): Map<string, ValidationBasisData> {
    const overrideBasis = this.rowData.find(
      element => element.id == 'overrideForBasis',
    )
    const mapBasisTransactionData = new Map<string, ValidationBasisData>()
    mapBasisTransactionData.set(
      'default',
      this.getValidationBasisDataForBasis(this.defaultBasisId, 'default'),
    )
    this.basisTypes
      ?.filter(
        basisEle => overrideBasis[this.getBasisProperty(basisEle.prefix)],
      )
      .forEach(basis => {
        mapBasisTransactionData.set(
          basis.prefix,
          this.getValidationBasisDataForBasis(basis.basisId, basis.prefix),
        )
      })
    return mapBasisTransactionData
  }

  getValidationBasisDataForBasis(
    basisId: number,
    basisPrefix: string,
  ): ValidationBasisData {
    const transData = {} as ValidationBasisData
    transData.basisId = basisId
    this.rowData
      .filter(ele => ele[this.getBasisProperty(basisPrefix)] !== '')
      .forEach(element => {
        switch (element.id) {
          case 'origTradeDate':
            transData.origTradeDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'origSettleDate':
            transData.origSettleDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'origPrice':
            transData.origPrice = element[this.getBasisProperty(basisPrefix)]
            break
          case 'origCommission':
            transData.origCommission =
              element[this.getBasisProperty(basisPrefix)]
            break
          case 'amortStartDate':
            transData.amortStartDate =
              element[this.getBasisProperty(basisPrefix)]
            break
          default:
            break
        }
      })
    return transData
  }

  nonComissionSecuritySelected() {
    const commission = this.getRowNode('origCommission')
    if (commission && commission.data.default == '')
      commission.setDataValue('default', 0.0)
  }

  setEditableStateForTpiFields(basisPrefix: string, state: boolean) {
    const colField = `isEditable${basisPrefix}`
    this.getRowNode('origTradeDate')?.setDataValue(colField, state)
    this.getRowNode('origSettleDate')?.setDataValue(colField, state)
    this.getRowNode('origPrice')?.setDataValue(colField, state)
    this.getRowNode('origCost')?.setDataValue(colField, state)
    this.getRowNode('origCommission')?.setDataValue(colField, state)
    this.getRowNode('accumulatedOtti')?.setDataValue(colField, state)
    this.getRowNode('proceeds')?.setDataValue(colField, state)
    this.getRowNode('yield')?.setDataValue(colField, state)
    this.getRowNode('accruedInterest')?.setDataValue(colField, state)
    this.getRowNode('factor')?.setDataValue(colField, state)
    this.getRowNode('daysImpaired')?.setDataValue(colField, state)
    this.getRowNode('accruedAtLoad')?.setDataValue(colField, state)
    this.getRowNode('amortStartCost')?.setDataValue(colField, state)
    this.getRowNode('amortStartDate')?.setDataValue(colField, state)
    this.getRowNode('amortStartPrice')?.setDataValue(colField, state)
    this.getRowNode('fxOriginalPurchaseRate')?.setDataValue(colField, state)
    this.getRowNode('fxOriginalCost')?.setDataValue(colField, state)
    this.getRowNode('fxAccumulatedOtti')?.setDataValue(colField, state)
    this.getRowNode('fxBookValueAtLoad')?.setDataValue(colField, state)
    this.getRowNode('fxAccruedAtLoad')?.setDataValue(colField, state)
    this.getRowNode('originalOriginationFee')?.setDataValue(colField, state)
    this.getRowNode('exitFee')?.setDataValue(colField, state)
    this.getRowNode('exitFeeReceivable')?.setDataValue(colField, state)
    this.getRowNode('drawFee')?.setDataValue(colField, state)
    this.getRowNode('prepaymentPenaltyFee')?.setDataValue(colField, state)
    this.getRowNode('prepaidInterest')?.setDataValue(colField, state)
    this.getRowNode('interestDue')?.setDataValue(colField, state)
    LOAN_FIELD_COLUMNS.forEach(({ field }) =>
      this.getRowNode(field)?.setDataValue(colField, state),
    )
    this.rowNodeMap
      .get('fxAccruedMarketDiscount')
      ?.setDataValue(colField, state)
    this.getRowNode('amortOverride')?.setDataValue(colField, state)
    this.getRowNode('lpCumulativeEarnings')?.setDataValue(colField, state)
    this.getRowNode('lpCumulativeDistributions')?.setDataValue(colField, state)
    this.getRowNode('returnOfInvestment')?.setDataValue(colField, state)
    this.getRowNode('returnOnInvestment')?.setDataValue(colField, state)
    if (
      !state ||
      this.getBasisProperty(basisPrefix) == 'tax' ||
      this.getBasisProperty(basisPrefix) == ''
    ) {
      this.getRowNode('taxDaysWashSale')?.setDataValue(colField, state)
      this.getRowNode('washSaleDeferredLoss')?.setDataValue(colField, state)
      this.rowNodeMap
        .get('accruedMarketDiscount')
        ?.setDataValue(colField, state)
      this.rowNodeMap
        .get('fxWashSaleDeferredLoss')
        ?.setDataValue(colField, state)
      this.getRowNode('straddleDeferredLoss')?.setDataValue(colField, state)
      this.getRowNode('fxStraddleDeferredLoss')?.setDataValue(colField, state)
      this.getRowNode('section1256DeferredLoss')?.setDataValue(colField, state)
      this.getRowNode('fxSection1256DeferredLoss')?.setDataValue(
        colField,
        state,
      )
    }
  }

  setEditableStateForMVTFlag(basisPrefix: string, state: boolean) {
    const colField = `isEditable${basisPrefix}`
    this.rowNodeMap?.get('marketValueTransfer')?.setDataValue(colField, state)
  }

  setEditableStateForExternalMVTFlag(basisPrefix: string, state: boolean) {
    const colField = `isEditable${basisPrefix}`
    this.rowNodeMap?.get('externalMvt')?.setDataValue(colField, state)
  }

  setEditableStateForExternalMVTFields(basisPrefix: string, state: boolean) {
    const colField = `isEditable${basisPrefix}`
    this.rowNodeMap?.get('externalMvtDate')?.setDataValue(colField, state)
    this.rowNodeMap?.get('externalMvtPrice')?.setDataValue(colField, state)
  }

  addDynamicColumn(basis: Basis) {
    this.columns.push(
      {
        field: `isEditable${basis.prefix}`,
        hide: true,
      },
      {
        field: initialLoadField(basis.prefix.toLowerCase()),
        headerClass: 'dynamic-col',
        headerName: basis.prefix,
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellRenderer: undefined,
        cellRendererSelector: params => {
          const isCheckboxRow = this.checkBoxCell(params)
          const component = isCheckboxRow
            ? EditableCheckboxRenderer
            : NgGridComponents.inlineEditDisplay
          return {
            component,
            params,
          }
        },
      },
    )
  }

  populateRowDataForBasisColumns(basisPrefix: string, state: boolean) {
    this.rowData
      .filter(element => element.id != 'overrideForBasis')
      .forEach(rowNode => {
        rowNode[this.getBasisProperty(basisPrefix)] = state
          ? rowNode.default
          : typeof rowNode.default == 'boolean'
          ? false
          : ''
        rowNode[`isEditable${basisPrefix}`] = state ? rowNode.isEditable : false
      })
  }

  adjustEqualDefaultBasis(
    basis: Basis,
    purchaseInfoData?: TransferPurchaseInfo,
    tpiMap?: Map<string, TransferPurchaseInfo>,
    mvtDataBasis?: MvtData,

    mvtMap?: Map<string, MvtData>,
  ) {
    if (
      basis.basisId == this.defaultBasisId ||
      (isEqual(
        omit(purchaseInfoData, ['basisId']),
        omit(tpiMap?.get(this.defaultBasisId.toString()), ['basisId']),
      ) &&
        isEqual(
          omit(mvtDataBasis, ['accountingBasis']),
          omit(mvtMap?.get(this.defaultBasisId.toString()), [
            'accountingBasis',
          ]),
        ))
    ) {
      const overrideBasis = this.rowData.find(
        element => element.id == 'overrideForBasis',
      )
      overrideBasis[basis.prefix.toLowerCase()] = false
      this.populateRowDataForBasisColumns(basis.prefix, false)
      overrideBasis[`isEditable${basis.prefix}`] = true
      this.rowData
        .filter(element => element.id != 'overrideForBasis')
        .forEach(element => {
          element[`isEditable${basis.prefix}`] = false
        })
    }
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }
}
